import xhr from 'xhr';

/** UI labels in Spanish; enum keys in English */
export const STATUS_LABELS = {
  pending: 'PENDIENTE',
  ready_for_payment: 'LISTO PARA PAGO',
  completed: 'COMPLETADA',
  retained: 'RETENIDO',
};

export const STATUS_COLORS = {
  pending: { bg: '#FEF3C7', color: '#92400E', borderColor: '#FCD34D' },
  ready_for_payment: { bg: '#DBEAFE', color: '#1E40AF', borderColor: '#93C5FD' },
  completed: { bg: '#E8E4FC', color: '#4840d4', borderColor: '#C4B5FD' },
  retained: { bg: '#D1FAE5', color: '#065F46', borderColor: '#6EE7B7' },
};

export const REASON_LABELS = {
  program_withdrawal: 'Retiro del programa',
  overpayment: 'Pagó de más',
  other: 'Otro',
};

export const REASON_OPTIONS = Object.entries(REASON_LABELS).map(([value, label]) => ({
  value,
  label,
}));

export const BANK_ACCOUNT_TYPE_OPTIONS = [
  { value: 'ahorros', label: 'Ahorros' },
  { value: 'corriente', label: 'Corriente' },
];

export const BANK_ACCOUNT_TYPE_LABELS = Object.fromEntries(
  BANK_ACCOUNT_TYPE_OPTIONS.map(({ value, label }) => [value, label])
);

export const TAB_STATUSES = [
  { key: 'pending', label: 'Pendientes' },
  { key: 'retained', label: 'Retenidos' },
  { key: 'ready_for_payment', label: 'Listos para pago' },
  { key: 'completed', label: 'Completadas' },
];

export function formatCaseId(id) {
  return `#${String(id).padStart(3, '0')}`;
}

export function formatCOP(amount) {
  if (amount == null || Number.isNaN(Number(amount))) return '—';
  return Number(amount).toLocaleString('es-CO', { style: 'currency', currency: 'COP', maximumFractionDigits: 0 });
}

export function formatCOPDisplay(amount) {
  const formatted = formatCOP(amount);
  return formatted === '—' ? formatted : `${formatted} COP`;
}

const PAYMENT_METHOD_LABELS = {
  payu: 'PayU',
  stripe: 'Tarjeta de crédito',
  wompi: 'Wompi',
  wompi_2: 'Wompi',
  bancolombia: 'Bancolombia',
  efecty: 'Efecty',
  paypal: 'PayPal',
  addi: 'Addi',
  sistecredito: 'Sistecrédito',
  bitcoins: 'Bitcoin',
  western_union: 'Western Union',
};

export function formatPaymentMethodLabel(method) {
  return PAYMENT_METHOD_LABELS[method] || method || '—';
}

export function formatPaymentDisplayId(payment) {
  if (!payment) return '—';
  return payment.id_payment ? `#${payment.id_payment}` : `#${payment.id}`;
}

export function formatStudentDocument(student) {
  if (!student?.identity) return '—';
  const type = student.identity_type?.short || student.identity_type?.name || 'CC';
  const digits = String(student.identity).replace(/\D/g, '');
  const formatted = digits.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
  return `${type} ${formatted}`;
}

const PAYMENT_STATUS_LABELS = {
  approved: 'Aprobado',
  factured: 'Facturado',
};

export function isPaymentFactured(payment) {
  return payment?.status === 'factured';
}

export function getPaymentInvoicingInfo(payment) {
  const factured = isPaymentFactured(payment);

  return {
    isFactured: factured,
    billingStatusLabel: factured ? 'Facturado' : 'Pendiente por facturar',
    requiresCreditNote: factured,
    creditNoteLabel: factured ? 'Requiere nota crédito' : 'No requiere nota crédito',
    noticeTitle: factured ? 'Pago facturado' : 'Pago sin facturar',
    noticeMessage: factured
      ? 'Este pago ya fue facturado. Debes gestionar nota crédito en la pestaña de facturas.'
      : 'Este pago aún no está facturado. No requiere proceso de nota crédito.',
  };
}

export function formatPaymentOption(payment) {
  const date = payment.payment_date || payment.createdAt;
  const dateStr = date ? new Date(date).toLocaleDateString('es-CO') : '—';
  const method = PAYMENT_METHOD_LABELS[payment.method] || payment.method || '—';
  const invoicingTag = isPaymentFactured(payment) ? 'Facturado · Nota crédito' : 'Sin facturar';
  const prefix = payment.id_payment ? `#${payment.id_payment}` : `#${payment.id}`;
  return `${prefix} · ${formatCOP(payment.amount)} · ${method} · ${dateStr} · ${invoicingTag}`;
}

export async function getRefundCounts() {
  const response = await xhr('/refunds/counts');
  return response.data;
}

export async function getRefunds({ page = 1, pageSize = 20, status, filters = {} } = {}) {
  const params = new URLSearchParams();
  params.append('pagination[page]', page);
  params.append('pagination[pageSize]', pageSize);
  params.append('sort[0]', 'createdAt:desc');

  if (status) {
    params.append('filters[status][$eq]', status);
  }

  if (filters.studentCode) {
    params.append('filters[student][id][$eq]', filters.studentCode.trim());
  }

  if (filters.programTypeId) {
    params.append('filters[program_type][id][$eq]', filters.programTypeId);
  }

  if (filters.reason) {
    params.append('filters[reason][$eq]', filters.reason);
  }

  params.append('populate[student][populate][user][fields][0]', 'first_name');
  params.append('populate[student][populate][user][fields][1]', 'last_name');
  params.append('populate[program_type][fields][0]', 'name');
  params.append('populate[payment][fields][0]', 'id');
  params.append('populate[payment][fields][1]', 'amount');

  const response = await xhr(`/refunds?${params.toString()}`);
  return response.data;
}

export async function getRefundById(id) {
  const response = await xhr(`/refunds/${id}`);
  const raw = response.data?.data || response.data;
  if (!raw) return raw;

  const events = raw.events || raw.attributes?.events || [];
  if (raw.attributes) {
    return { id: raw.id, ...raw.attributes, events };
  }

  return { ...raw, events };
}

export async function getEligiblePayments(studentId) {
  const response = await xhr(`/refunds/eligible-payments/${studentId}`);
  return response.data?.data || response.data || [];
}

export async function createRefund(data) {
  const response = await xhr.post('/refunds', { data });
  return response.data;
}

export async function markRetained(id, note) {
  const response = await xhr.post(`/refunds/${id}/mark-retained`, { note });
  return response.data;
}

export async function proceedRefund(id, note) {
  const response = await xhr.post(`/refunds/${id}/proceed-refund`, { note });
  return response.data;
}

export async function completeRefund(id, refund_date) {
  const response = await xhr.post(`/refunds/${id}/complete`, { refund_date });
  return response.data;
}

export async function uploadRefundFile({ file, refId, field }) {
  const formData = new FormData();
  formData.append('files', file);
  formData.append('ref', 'api::refund.refund');
  formData.append('refId', refId);
  formData.append('field', field);

  const response = await xhr('/upload', {
    method: 'post',
    data: formData,
    headers: { 'Content-Type': 'multipart/form-data' },
  });

  return response.data;
}

export async function searchStudents(query) {
  const term = query.trim();
  if (!term) return [];

  const params = new URLSearchParams();
  params.append('pagination[limit]', '10');
  params.append('populate[user][fields][0]', 'first_name');
  params.append('populate[user][fields][1]', 'last_name');
  params.append('populate[user][fields][2]', 'email');
  params.append('populate', 'identity_type');
  params.append('populate', 'program_type');

  if (/^\d+$/.test(term)) {
    params.append('filters[id][$containsi]', term);
  } else {
    params.append('filters[$or][0][user][first_name][$containsi]', term);
    params.append('filters[$or][1][user][last_name][$containsi]', term);
    params.append('filters[$or][2][user][email][$containsi]', term);
  }

  const response = await xhr(`/students?${params.toString()}`);
  return response.data?.data || [];
}

export async function searchStudentByCode(code) {
  const response = await xhr(
    `/students?filters[id][$eq]=${parseInt(code, 10)}&populate[user][fields][0]=first_name&populate[user][fields][1]=last_name&populate=identity_type&populate=program_type`
  );
  return response.data?.data?.[0] || null;
}

export async function getProgramTypes() {
  const response = await xhr('/program-types?filters[active][$eq]=true&pagination[limit]=-1');
  return response.data?.data || [];
}

export function getStudentShortName(student) {
  if (!student?.user) return '—';
  const first = student.user.first_name || '';
  const last = student.user.last_name || '';
  const lastInitial = last ? `${last.charAt(0)}.` : '';
  const short = `${first} ${lastInitial}`.trim();
  return short || '—';
}

export function formatIdentityNumber(identity) {
  if (!identity) return '—';
  const digits = String(identity).replace(/\D/g, '');
  return digits.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
}

export function formatMaskedBankAccount(accountNumber) {
  if (!accountNumber) return '—';
  const digits = String(accountNumber).replace(/\D/g, '');
  if (!digits) return '—';
  if (digits.length <= 4) return `****${digits}`;
  return `****${digits.slice(-4)}`;
}

export function getBankAccountTypeLabel(type) {
  return BANK_ACCOUNT_TYPE_LABELS[type] || type || '—';
}

export function formatStudentOption(student) {
  const name = getStudentDisplayName(student);
  return `${student.id} · ${name}`;
}

export function getStudentDisplayName(student) {
  if (!student?.user) return `Estudiante ${student?.id || ''}`;
  return `${student.user.first_name || ''} ${student.user.last_name || ''}`.trim();
}

export function getUserDisplayName(user) {
  if (!user) return 'Usuario desconocido';
  const name = `${user.first_name || ''} ${user.last_name || ''}`.trim();
  return name || user.email || 'Usuario desconocido';
}

export const REFUND_BUSINESS_DAYS_LIMIT = 15;
export const REFUND_DEADLINE_WARNING_DAYS = 3;

const ACTIVE_DEADLINE_STATUSES = new Set(['pending', 'ready_for_payment']);

export const DEADLINE_STATUS_LABELS = {
  ok: 'En plazo',
  warning: 'Por vencer',
  overdue: 'Vencido',
};

export const DEADLINE_STATUS_COLORS = {
  ok: { bg: '#ECFDF5', color: '#065F46', borderColor: '#6EE7B7' },
  warning: { bg: '#FEF3C7', color: '#92400E', borderColor: '#FCD34D' },
  overdue: { bg: '#FEE2E2', color: '#991B1B', borderColor: '#FCA5A5' },
};

function startOfLocalDay(date) {
  const d = new Date(date);
  if (Number.isNaN(d.getTime())) return null;
  d.setHours(0, 0, 0, 0);
  return d;
}

function isWeekday(date) {
  const day = date.getDay();
  return day >= 1 && day <= 5;
}

/** Adds N Mon–Fri days after start (registration day is not counted). */
export function addBusinessDays(startDate, businessDays) {
  const start = startOfLocalDay(startDate);
  if (!start || businessDays < 1) return null;

  const cursor = new Date(start);
  let added = 0;

  while (added < businessDays) {
    cursor.setDate(cursor.getDate() + 1);
    if (isWeekday(cursor)) added += 1;
  }

  return cursor;
}

function businessDaysUntilDue(fromDate, dueDate) {
  const from = startOfLocalDay(fromDate);
  const due = startOfLocalDay(dueDate);
  if (!from || !due) return null;
  if (from > due) return -1;

  let count = 0;
  const cursor = new Date(from);
  cursor.setDate(cursor.getDate() + 1);

  while (cursor <= due) {
    if (isWeekday(cursor)) count += 1;
    cursor.setDate(cursor.getDate() + 1);
  }

  return count;
}

export function getRefundDeadlineInfo(refund) {
  const status = refund?.status;
  const createdAt = refund?.createdAt;

  if (!ACTIVE_DEADLINE_STATUSES.has(status) || !createdAt) {
    return { applies: false };
  }

  const registeredAt = startOfLocalDay(createdAt);
  const dueDate = addBusinessDays(createdAt, REFUND_BUSINESS_DAYS_LIMIT);
  const today = startOfLocalDay(new Date());

  if (!registeredAt || !dueDate || !today) {
    return { applies: false };
  }

  const isOverdue = today > dueDate;
  const daysRemaining = isOverdue ? 0 : businessDaysUntilDue(today, dueDate);

  let deadlineStatus = 'ok';
  if (isOverdue) {
    deadlineStatus = 'overdue';
  } else if (daysRemaining <= REFUND_DEADLINE_WARNING_DAYS) {
    deadlineStatus = 'warning';
  }

  let statusDetail = null;
  if (deadlineStatus === 'warning') {
    statusDetail =
      daysRemaining === 0
        ? 'Vence hoy'
        : `${daysRemaining} día${daysRemaining === 1 ? '' : 's'} hábil${daysRemaining === 1 ? '' : 'es'} restante${daysRemaining === 1 ? '' : 's'}`;
  }

  return {
    applies: true,
    registeredAt,
    dueDate,
    dueDateFormatted: formatRefundDate(dueDate),
    registeredAtFormatted: formatRefundDate(registeredAt),
    deadlineStatus,
    statusLabel: DEADLINE_STATUS_LABELS[deadlineStatus],
    statusDetail,
    businessDaysRemaining: isOverdue ? 0 : daysRemaining,
    businessDaysLimit: REFUND_BUSINESS_DAYS_LIMIT,
  };
}

export function formatRefundDate(dateStr) {
  if (!dateStr) return '—';
  const date = new Date(dateStr);
  if (Number.isNaN(date.getTime())) return '—';
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();
  return `${day}-${month}-${year}`;
}

export function formatRefundDateTime(dateStr) {
  if (!dateStr) return '—';
  const date = new Date(dateStr);
  if (Number.isNaN(date.getTime())) return '—';

  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();

  let hours = date.getHours();
  const minutes = String(date.getMinutes()).padStart(2, '0');
  const ampm = hours >= 12 ? 'PM' : 'AM';
  hours = hours % 12 || 12;

  return `${day}-${month}-${year} · ${hours}:${minutes} ${ampm}`;
}

export function previewRefundAmount(paidAmount, discountPercent) {
  const paid = Number(paidAmount);
  if (!Number.isFinite(paid)) return null;

  const raw = discountPercent === '' || discountPercent == null ? 0 : Number(discountPercent);
  const percent = Number.isFinite(raw) ? raw : 0;
  const discount = Math.round(paid * (percent / 100));
  return paid - discount;
}
